<script setup lang="ts">
import LeHeading from "@dsk/components/Heading/LeHeading.vue";
import { computed } from "vue";

import { DictsysArticlePhraseHelper } from "@/helpers";
import type { DictsysPhrase, DictsysPhraseReference } from "@/types";

import LeArticleHoverableArea from "./LeArticleHoverableArea.vue";
import LeArticlePhraseElement from "./LeArticlePhraseElement.vue";
import LeArticlePhraseLink from "./LeArticlePhraseLink.vue";

const props = defineProps<{
  phrases: DictsysPhrase[];
  sourceLanguage: string;
  targetLanguage: string;
}>();

const commonReferences = computed<DictsysPhraseReference[]>(() =>
  DictsysArticlePhraseHelper.getCommonReferences(props.phrases)
);

const phraseGroups = computed<DictsysArticlePhraseHelper.DictsysPhraseGroup[]>(() =>
  DictsysArticlePhraseHelper.getPhraseGroups(props.phrases)
);
</script>

<template>
  <div class="le-article-phrases">
    <LeHeading tag="h4" class="le-article-phrases__heading" lang="da">Fraseologisk ordbog</LeHeading>

    <div v-if="commonReferences.length" class="le-article-phrases__references" :lang="props.sourceLanguage">
      <LeArticlePhraseLink text="Se også:" text-language="da" :references="commonReferences" />
    </div>

    <ul class="le-article-phrases__list">
      <template v-for="(phraseGroup, phraseGroupIndex) in phraseGroups">
        <li
          v-if="phraseGroup.translations.length > 0"
          :key="`${phraseGroupIndex}-translation`"
          class="le-article-phrases__list-bullet"
          :class="{
            'le-article-phrases__list-bullet--last':
              phraseGroup.examples.length === 0 && phraseGroupIndex === phraseGroups.length - 1
          }"
        >
          <LeArticleHoverableArea>
            <LeArticlePhraseElement
              :phrase="phraseGroup.phrase"
              :targets="phraseGroup.translations"
              type="translation"
              :source-language="props.sourceLanguage"
              :target-language="props.targetLanguage"
            />
          </LeArticleHoverableArea>
        </li>
        <li
          v-if="phraseGroup.examples.length > 0"
          :key="`${phraseGroupIndex}-example`"
          class="le-article-phrases__list-bullet"
          :class="{
            'le-article-phrases__list-bullet--last': phraseGroupIndex === phraseGroups.length - 1
          }"
        >
          <LeArticleHoverableArea>
            <LeArticlePhraseElement
              :phrase="phraseGroup.phrase"
              :targets="phraseGroup.examples"
              type="example"
              :source-language="props.sourceLanguage"
              :target-language="props.targetLanguage"
            />
          </LeArticleHoverableArea>
        </li>
      </template>
    </ul>
  </div>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all";

.le-article-phrases {
  &__heading,
  &__references {
    margin-bottom: 8px;
  }

  &__list {
    display: flex;
    flex-direction: column;
    margin: 0;
    padding-left: 15px;
    color: @colors[@secondary][800];
    font-size: 17px;
  }

  &__list-bullet {
    margin-bottom: 12px;

    #media-breakpoint-up(md, {
      margin-bottom: 14px;
    });

    &--last {
      margin-bottom: 0;
    }
  }
}
</style>
